import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";

export const fundFamilyConfigurationCreditFacilityRoutes: RouteObject = {
  id: "fundFamilyConfigurationCreditFacilities",
  path: "credit-facility",
  children: [
    {
      id: "fundFamilyConfigurationCreditFacilitiesIndex",
      index: true,
      lazy: async () => {
        const { CreditFacilitySetupIndexPage } = await import(
          "features/credit-facility/setup/CreditFacilitySetupIndexPage"
        );
        return { Component: CreditFacilitySetupIndexPage };
      },
      handle: {
        showSidebar: true,
        description: "Configuration - Credit Facilities",
      },
    },
    {
      id: "fundFamilyConfigurationCreditFacilitiesCreditFacility",
      path: ":creditFacilityId/*",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { CreditFacilitySettingsPage } = await import(
          "features/credit-facility/settings/CreditFacilitySettingsPage"
        );
        return { Component: CreditFacilitySettingsPage };
      },
      children: [
        {
          id: "fundFamilyConfigurationCreditFacilitiesCreditFacilityDetails",
          path: "",
          lazy: async () => {
            const { CreditFacilitySettingsDetail } = await import(
              "features/credit-facility/settings/CreditFacilitySettingsDetail"
            );
            return { Component: CreditFacilitySettingsDetail };
          },
          handle: {
            title: i18n.t("creditFacility:settings.creditFacilitySettings"),
          },
        },
        {
          id: "fundFamilyConfigurationCreditFacilitiesCreditFacilityLoan",
          path: "loans",
          lazy: async () => {
            const { CreditFacilitySettingsLoans } = await import(
              "features/credit-facility/settings/CreditFacilitySettingsLoans"
            );
            return { Component: CreditFacilitySettingsLoans };
          },
          handle: {
            title: i18n.t("creditFacility:settings.loanConfiguration"),
          },
        },
        {
          id: "fundFamilyConfigurationCreditFacilitiesCreditFacilitySettingsBorrowingBase",
          path: "borrowing-base",
          lazy: async () => {
            const { CreditFacilitySettingsBorrowingBase } = await import(
              "features/credit-facility/settings/CreditFacilitySettingsBorrowingBase"
            );
            return { Component: CreditFacilitySettingsBorrowingBase };
          },
          handle: {
            title: i18n.t("creditFacility:settings.borrowingBase"),
          },
        },
      ],
    },
    {
      id: "fundFamilyConfigurationCreditFacilitiesCreditFacilitySettingsBorrowingBaseEdit",
      path: ":creditFacilityId/borrowing-base/:borrowingBaseGroupId",
      lazy: async () => {
        const { CreditFacilitySettingsBorrowingBaseEdit } = await import(
          "features/credit-facility/settings/CreditFacilitySettingsBorrowingBaseEdit"
        );
        return {
          Component: CreditFacilitySettingsBorrowingBaseEdit,
        };
      },
    },
    {
      id: "fundFamilyConfigurationCreditFacilitiesCreditFacilityLoanVersionPage",
      path: ":creditFacilityId/loans/:loanId",
      lazy: async () => {
        const { CreditFacilitySettingsLoanVersion } = await import(
          "features/credit-facility/settings/CreditFacilitySettingsLoan/CreditFacilitySettingsLoanVersion"
        );
        return { Component: CreditFacilitySettingsLoanVersion };
      },
      handle: {
        title: "Loan",
      },
    },
    {
      id: "fundFamilyConfigurationCreditFacilitiesCreditFacilityLoanCreate",
      path: ":creditFacilityId/loans/new",
      lazy: async () => {
        const { CreditFacilityLoanCreate } = await import(
          "features/credit-facility/settings/CreditFacilitySettingsLoanCreate"
        );
        return { Component: CreditFacilityLoanCreate };
      },
      handle: {
        title: "",
      },
    },
  ],
};

export const creditFacilityRoutes: RouteObject = {
  id: "creditFacilities",
  path: "credit-facility",
  children: [
    {
      id: "creditFacilitiesIndex",
      index: true,
      handle: {
        showSidebar: true,
        description: "Credit Facilities Dashboard",
      },
      lazy: async () => {
        const { CreditFacilityIndexPage } = await import(
          "features/credit-facility/CreditFacilityIndexPage"
        );
        return { Component: CreditFacilityIndexPage };
      },
    },
    {
      id: "creditFacilitiesCreditFacility",
      path: ":creditFacilityId",
      children: [
        {
          id: "creditFacilitiesCreditFacilityDashboard",
          path: "",
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { CreditFacilityPageTabs } = await import(
              "features/credit-facility/tabs/CreditFacilityPageTabs"
            );
            return { Component: CreditFacilityPageTabs };
          },
          children: [
            {
              id: "creditFacilitiesCreditFacilityPrincipal",
              path: "principal",
              lazy: async () => {
                const { CreditFacilityPrincipalTab } = await import(
                  "features/credit-facility/tabs/CreditFacilityPrincipalTab"
                );
                return { Component: CreditFacilityPrincipalTab };
              },
              handle: {
                title: "Principal",
                description: "Credit Facilities Dashboard - Principal Section",
              },
            },
            {
              id: "creditFacilitiesCreditFacilityExpenses",
              path: "expenses",
              lazy: async () => {
                const { CreditFacilityExpenseTab } = await import(
                  "features/credit-facility/tabs/CreditFacilityExpenseTab"
                );
                return { Component: CreditFacilityExpenseTab };
              },
              handle: {
                title: "Expenses",
                description: "Credit Facilities Dashboard - Expenses Section",
              },
            },
            {
              id: "creditFacilitiesCreditFacilityBorrowingBase",
              path: "borrowing-base",
              lazy: async () => {
                const { CreditFacilityBorrowingBaseTab } = await import(
                  "features/credit-facility/tabs/CreditFacilityBorrowingBaseTab"
                );
                return { Component: CreditFacilityBorrowingBaseTab };
              },
              handle: {
                title: "Borrowing Base",
                description:
                  "Credit Facilities Dashboard - Borrowing Base Section",
              },
            },
          ],
        },
        {
          id: "creditFacilitiesCreditFacilitySetup",
          path: "setup",
          lazy: async () => {
            const { CreditFacilitySetupPage } = await import(
              "features/credit-facility/setup/CreditFacilitySetupPage"
            );
            return { Component: CreditFacilitySetupPage };
          },
          children: [
            {
              id: "creditFacilitiesCreditFacilitySetupDetails",
              path: "",
              lazy: async () => {
                const { CreditFacilitySetupDetails } = await import(
                  "features/credit-facility/setup/CreditFacilitySetupDetails"
                );
                return { Component: CreditFacilitySetupDetails };
              },
              handle: { title: i18n.t("creditFacility:setup.details.title") },
            },
            {
              id: "creditFacilitiesCreditFacilitySetupCommitments",
              path: "commitments",
              lazy: async () => {
                const { CreditFacilitySetupCommitments } = await import(
                  "features/credit-facility/setup/CreditFacilitySetupCommitments"
                );
                return { Component: CreditFacilitySetupCommitments };
              },
              handle: {
                title: i18n.t("creditFacility:setup.commitments.title"),
              },
            },
            {
              id: "creditFacilitiesCreditFacilitySetupLoan",
              path: "loan",
              lazy: async () => {
                const { CreditFacilitySetupLoan } = await import(
                  "features/credit-facility/setup/CreditFacilitySetupLoan"
                );
                return { Component: CreditFacilitySetupLoan };
              },
              handle: { title: i18n.t("creditFacility:setup.loan.breadcrumb") },
            },
            {
              id: "creditFacilitiesCreditFacilitySetupBorrowingBase",
              path: "borrowing-base",
              lazy: async () => {
                const { CreditFacilitySetupBorrowingBase } = await import(
                  "features/credit-facility/setup/CreditFacilitySetupBorrowingBase"
                );
                return { Component: CreditFacilitySetupBorrowingBase };
              },
              handle: {
                title: i18n.t("creditFacility:setup.borrowingBase.breadcrumb"),
              },
            },
            {
              id: "creditFacilitiesCreditFacilitySetupBorrowingBaseReview",
              path: "review",
              lazy: async () => {
                const { CreditFacilitySetupBorrowingBaseSummary } =
                  await import(
                    "features/credit-facility/setup/CreditFacilitySetupBorrowingBaseSummary"
                  );
                return { Component: CreditFacilitySetupBorrowingBaseSummary };
              },
              handle: {
                title: i18n.t("creditFacility:setup.summary.breadcrumb"),
              },
            },
          ],
        },
        {
          id: "creditFacilitiesCreditFacilitySetupInvestorBorrowingBase",
          path: "setup/borrowing-base/:borrowingBaseGroupId/investors",
          lazy: async () => {
            const { BorrowingBaseInvestorPage } = await import(
              "features/credit-facility/borrowing-base/InvestorPage"
            );
            return {
              Component: BorrowingBaseInvestorPage,
            };
          },
        },
        {
          id: "creditFacilitiesCreditFacilityEvent",
          path: ":fundFamilyEventId",
          children: [
            {
              id: "creditFacilitiesCreditFacilityEventDrawdown",
              handle: {
                title: i18n.t("creditFacility:drawdown.title"),
                breadcrumb: i18n.t("creditFacility:drawdown.breadcrumb"),
              },
              path: "drawdown",
              lazy: async () => {
                const { CreditFacilityPrincipalEventPage } = await import(
                  "features/credit-facility/CreditFacilityPrincipalEventPage"
                );
                return { Component: CreditFacilityPrincipalEventPage };
              },
              children: [
                {
                  id: "creditFacilitiesCreditFacilityEventDrawdownUpdate",
                  index: true,
                  path: "",
                  lazy: async () => {
                    const { CreditFacilityDrawdownPage } = await import(
                      "features/credit-facility/drawdown/CreditFacilityDrawdownPage"
                    );
                    return { Component: CreditFacilityDrawdownPage };
                  },
                  handle: {
                    title: i18n.t("creditFacility:drawdown.title"),
                    breadcrumb: i18n.t(
                      "creditFacility:drawdown.drawdownAmount",
                    ),
                  },
                },
                {
                  id: "creditFacilitiesCreditFacilityEventDrawdownReview",
                  path: "review",
                  lazy: async () => {
                    const { CreditFacilitySummary } = await import(
                      "features/credit-facility/CreditFacilitySummary"
                    );
                    return { Component: CreditFacilitySummary };
                  },
                  handle: {
                    title: i18n.t("creditFacility:drawdown.reviewDrawdown"),
                    breadcrumb: i18n.t("creditFacility:drawdown.review"),
                  },
                },
              ],
            },
            {
              id: "creditFacilitiesCreditFacilityEventPaydownLegacy",
              path: "legacy-paydown",
              lazy: async () => {
                const { CreditFacilityPrincipalEventPage } = await import(
                  "features/credit-facility/CreditFacilityPrincipalEventPage"
                );
                return { Component: CreditFacilityPrincipalEventPage };
              },
              children: [
                {
                  id: "creditFacilitiesCreditFacilityEventPaydownUpdateLegacy",
                  path: "",
                  lazy: async () => {
                    const { CreditFacilityPaydownDetails } = await import(
                      "features/credit-facility/legacy-paydown/CreditFacilityPaydownDetails"
                    );
                    return { Component: CreditFacilityPaydownDetails };
                  },
                  handle: {
                    title: i18n.t("creditFacility:paydown.title"),
                    breadcrumb: i18n.t("creditFacility:paydown.breadcrumb"),
                  },
                },
                {
                  id: "creditFacilitiesCreditFacilityEventPaydownReviewLegacy",
                  path: "review",
                  lazy: async () => {
                    const { CreditFacilitySummary } = await import(
                      "features/credit-facility/CreditFacilitySummary"
                    );
                    return { Component: CreditFacilitySummary };
                  },
                  handle: {
                    title: i18n.t("creditFacility:paydown.review"),
                  },
                },
              ],
            },
            {
              id: "creditFacilitiesCreditFacilityEventPaydown",
              path: "paydown",
              lazy: async () => {
                const { CreditFacilityPrincipalEventPage } = await import(
                  "features/credit-facility/CreditFacilityPrincipalEventPage"
                );
                return { Component: CreditFacilityPrincipalEventPage };
              },
              children: [
                {
                  id: "creditFacilitiesCreditFacilityEventPaydownUpdate",
                  path: "",
                  lazy: async () => {
                    const { CreditFacilityPaydownPage } = await import(
                      "features/credit-facility/CreditFacilityPaydownPage"
                    );
                    return { Component: CreditFacilityPaydownPage };
                  },
                  handle: {
                    title: i18n.t("creditFacility:paydown.title"),
                    breadcrumb: i18n.t("creditFacility:paydown.breadcrumb"),
                  },
                },
                {
                  id: "creditFacilitiesCreditFacilityEventPaydownReview",
                  path: "review",
                  lazy: async () => {
                    const { CreditFacilitySummary } = await import(
                      "features/credit-facility/CreditFacilitySummary"
                    );
                    return { Component: CreditFacilitySummary };
                  },
                  handle: {
                    breadcrumb: i18n.t("creditFacility:paydown.review"),
                    title: i18n.t("creditFacility:paydown.reviewPaydown"),
                  },
                },
              ],
            },
            {
              id: "creditFacilitiesCreditFacilityEventReallocation",
              handle: {
                title: i18n.t("creditFacility:reallocation.title"),
              },
              path: "reallocation",
              lazy: async () => {
                const { CreditFacilityPrincipalEventPage } = await import(
                  "features/credit-facility/CreditFacilityPrincipalEventPage"
                );
                return { Component: CreditFacilityPrincipalEventPage };
              },
              children: [
                {
                  id: "creditFacilitiesCreditFacilityEventReallocationSelect",
                  index: true,
                  path: "",
                  handle: {
                    title: i18n.t(
                      "creditFacility:reallocation.selectTranches.title",
                    ),
                    breadcrumb: i18n.t(
                      "creditFacility:reallocation.selectTranches.breadcrumb",
                    ),
                  },
                  lazy: async () => {
                    const { CreditFacilityReallocationSelectTranches } =
                      await import(
                        "features/credit-facility/reallocation/CreditFacilityReallocationSelectTranches"
                      );
                    return {
                      Component: CreditFacilityReallocationSelectTranches,
                    };
                  },
                },
                {
                  id: "creditFacilitiesCreditFacilityEventReallocationSplit",
                  path: "split",
                  handle: {
                    title: i18n.t("creditFacility:reallocation.split.title"),
                    breadcrumb: i18n.t(
                      "creditFacility:reallocation.split.breadcrumb",
                    ),
                  },
                  lazy: async () => {
                    const { CreditFacilityReallocationSplit } = await import(
                      "features/credit-facility/reallocation/CreditFacilityReallocationSplit"
                    );
                    return { Component: CreditFacilityReallocationSplit };
                  },
                },
                {
                  id: "creditFacilitiesCreditFacilityEventReallocationReview",
                  path: "review",
                  lazy: async () => {
                    const { CreditFacilitySummary } = await import(
                      "features/credit-facility/CreditFacilitySummary"
                    );
                    return { Component: CreditFacilitySummary };
                  },
                  handle: {
                    title: i18n.t("creditFacility:reallocation.summary.title"),
                    breadcrumb: i18n.t(
                      "creditFacility:reallocation.summary.breadcrumb",
                    ),
                  },
                },
              ],
            },
            {
              id: "creditFacilitiesCreditFacilityEventMergeTranches",
              handle: {
                title: i18n.t("creditFacility:merge.title"),
              },
              path: "merge-tranche",
              lazy: async () => {
                const { CreditFacilityPrincipalEventPage } = await import(
                  "features/credit-facility/CreditFacilityPrincipalEventPage"
                );
                return { Component: CreditFacilityPrincipalEventPage };
              },
              children: [
                {
                  id: "creditFacilitiesCreditFacilityEventMergeTranche",
                  index: true,
                  path: "",
                  handle: {
                    title: i18n.t("creditFacility:merge.selectTranches.title"),
                    breadcrumb: i18n.t(
                      "creditFacility:merge.selectTranches.breadcrumb",
                    ),
                  },
                  lazy: async () => {
                    const { CreditFacilityMergeTranchesSelectPage } =
                      await import(
                        "features/credit-facility/merge-tranches/CreditFacilityMergeTranchesSelectPage"
                      );
                    return {
                      Component: CreditFacilityMergeTranchesSelectPage,
                    };
                  },
                },
                {
                  id: "creditFacilitiesCreditFacilityEventMergeTrancheReview",
                  path: "review",
                  lazy: async () => {
                    const { CreditFacilityMergeTranchesReview } = await import(
                      "features/credit-facility/merge-tranches/CreditFacilityMergeTranchesReview"
                    );
                    return { Component: CreditFacilityMergeTranchesReview };
                  },
                  handle: {
                    title: i18n.t("creditFacility:merge.review.title"),
                    breadcrumb: i18n.t(
                      "creditFacility:merge.review.breadcrumb",
                    ),
                  },
                },
              ],
            },
          ],
        },
        {
          id: "creditFacilitiesCreditFacilityExpenseEvent",
          handle: {
            title: i18n.t("creditFacility:postExpense.title"),
          },
          path: ":expenseEventId/expenses",
          lazy: async () => {
            const { CreditFacilityExpenseWorkflow } = await import(
              "features/credit-facility/expenses/workflow/CreditFacilityExpenseWorkflow"
            );
            return { Component: CreditFacilityExpenseWorkflow };
          },
          children: [
            {
              id: "creditFacilitiesCreditFacilityExpenseEventSetup",
              path: "",
              index: true,
              handle: {
                title: i18n.t("creditFacility:postExpense.setup.title"),
                breadcrumb: i18n.t(
                  "creditFacility:postExpense.setup.breadcrumb",
                ),
              },
              lazy: async () => {
                const { CreditFacilityExpenseWorkflowSetup } = await import(
                  "features/credit-facility/expenses/workflow/CreditFacilityExpenseWorkflowSetup"
                );
                return { Component: CreditFacilityExpenseWorkflowSetup };
              },
            },
            {
              id: "creditFacilitiesCreditFacilityExpenseEventBreakdown",
              path: "breakdown",
              handle: {
                title: i18n.t("creditFacility:postExpense.breakdown.title"),
                breadcrumb: i18n.t(
                  "creditFacility:postExpense.breakdown.breadcrumb",
                ),
              },
              lazy: async () => {
                const { CreditFacilityExpenseWorkflowBreakdown } = await import(
                  "features/credit-facility/expenses/workflow/CreditFacilityExpenseWorkflowBreakdown"
                );
                return {
                  Component: CreditFacilityExpenseWorkflowBreakdown,
                };
              },
            },
            {
              id: "creditFacilitiesCreditFacilityExpenseEventReview",
              path: "review",
              handle: {
                title: i18n.t("creditFacility:postExpense.review.title"),
                breadcrumb: i18n.t(
                  "creditFacility:postExpense.review.breadcrumb",
                ),
              },
              lazy: async () => {
                const { CreditFacilityExpenseWorkflowReview } = await import(
                  "features/credit-facility/expenses/workflow/CreditFacilityExpenseWorkflowReview"
                );
                return {
                  Component: CreditFacilityExpenseWorkflowReview,
                };
              },
            },
          ],
        },
      ],
    },
  ],
};
